import { useMemo } from 'react';
import { getCurrentUser } from '@/services/api/auth';

interface UsePermissionsReturn {
    permissions: Set<string>;
    hasPermission: (code: string) => boolean;
    hasAnyPermission: (...codes: string[]) => boolean;
    hasAllPermissions: (...codes: string[]) => boolean;
    isOwner: boolean;
    isSuperAdmin: boolean;
    isPartner: boolean;
    isEmployee: boolean;
}

export function usePermissions(): UsePermissionsReturn {
    const user = getCurrentUser();

    const isSuperAdmin = !!(user?.owner === true || (user?.owner as unknown) === 1);
    const isEmployee = user?.user_type === 'employee';
    const isPartner = !isSuperAdmin && !isEmployee;
    const isOwner = isSuperAdmin;

    const permissions = useMemo(() => {
        const userPerms = (user as any)?.permissions || [];
        return new Set<string>(userPerms);
    }, [user]);

    const hasPermission = (code: string): boolean => {
        if (isSuperAdmin || isPartner) return true;
        if (permissions.has('*')) return true;
        return permissions.has(code);
    };

    const hasAnyPermission = (...codes: string[]): boolean => {
        return codes.some((code) => hasPermission(code));
    };

    const hasAllPermissions = (...codes: string[]): boolean => {
        return codes.every((code) => hasPermission(code));
    };

    return {
        permissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isOwner,
        isSuperAdmin,
        isPartner,
        isEmployee,
    };
}
